<script setup lang="ts">
import DataTable, {
  type DataTableFilterEvent,
  type DataTableFilterMeta,
  type DataTablePageEvent,
  type DataTableRowClickEvent,
  type DataTableSortEvent,
} from 'primevue/datatable';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import type { DataTableData } from '@composables/useDataTable.ts';
import { useDataTableStore } from '../../_00_elements/tools/store/pinia/useDataTableStore';
import { useLoading } from '../../_00_elements/tools/composables/useLoading.ts';
import { transchoice } from '../../_00_elements/translation';

const dataStore = useDataTableStore();
const { data } = storeToRefs(dataStore);
const { setDataKey, getData, onPageChange, onSortChange, onFilterChange } = dataStore;

const { isLoading, startLoading, stopLoading } = useLoading();

const emit = defineEmits<{(e: 'row-clicked', originalEvent: DataTableRowClickEvent): void;
}>();

const hasItems = computed<boolean>(() => (data.value?.items?.length ?? 0) > 0);

const props = withDefaults(defineProps<{
  dataKey?: string,
  filters?: DataTableFilterMeta,
  scrollable?: boolean,
  withPaginatorMetaData?: boolean,
}>(), {
  dataKey: 'id',
  filters: undefined,
  scrollable: false,
  withPaginatorMetaData: true,
});

setDataKey(props.dataKey);
const filters = ref<DataTableFilterMeta|undefined>(props.filters);

const handlePageChange = async (event: DataTablePageEvent): Promise<void> => {
  startLoading();
  await onPageChange(event);
  stopLoading();
};

const handleSortChange = async (event: DataTableSortEvent): Promise<void> => {
  startLoading();
  await onSortChange(event);
  stopLoading();
};

const handleFilterChange = async (event: DataTableFilterEvent): Promise<void> => {
  startLoading();
  await onFilterChange(event);
  stopLoading();
};

const handleRowClick = (e: DataTableRowClickEvent): void => {
  emit('row-clicked', e);
};

const fetchData = async (): Promise<void> => {
  startLoading();
  const responseData: DataTableData | null = await getData();

  if (responseData) {
    data.value = responseData;
  }

  stopLoading();
};

onMounted(fetchData);
</script>

<template>
  <div v-if="data">
    <slot v-if="!hasItems" name="empty-state" />
    <DataTable
      v-else
      v-model:filters="filters"
      :filter-display="filters ? 'row' : undefined"
      class="datatable"
      :value="data.items"
      edit-mode="row"
      removable-sort
      paginator
      lazy
      :scrollable="props.scrollable"
      :first="data.meta?.from"
      :total-records="data.meta?.total"
      :rows="data.meta?.perPage"
      :data-key="props.dataKey"
      :loading="isLoading"
      @row-click="handleRowClick"
      @page="handlePageChange"
      @sort="handleSortChange"
      @filter="handleFilterChange"
    >
      <slot />

      <template #loading>
        <div class="datatable-overlay" />
      </template>
      <template v-if="withPaginatorMetaData" #paginatorstart>
        {{
          transchoice('datatable.paginator.metadata', {
            from: data.meta?.from,
            count: data.meta?.to
          })
        }}
      </template>
    </DataTable>
  </div>
</template>
